import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import ApiService from "../../services/ApiService";
import Loader from "../../components/Loader";
import GoogleMapSelector from "../../components/GoogleMapSelector";

const TABS = [
  { key: 'general',       label: 'General',        icon: 'bi-gear' },
  { key: 'payments',      label: 'Payments',       icon: 'bi-credit-card' },
  { key: 'operating',     label: 'Operating Hours', icon: 'bi-clock' },
  { key: 'map',           label: 'Venue Location', icon: 'bi-geo-alt' },
  { key: 'cancellation',  label: 'Cancellation',   icon: 'bi-x-circle' },
  { key: 'notifications', label: 'Notifications',  icon: 'bi-envelope' },
];

// Simple, permissive email check — good enough to catch typos in the CSV list
// without rejecting valid-but-unusual addresses.
const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const DAYS = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
];

// working_hours stores open/close as numbers (minutes since midnight), per settingModel.
// The clock inputs use 24-hour "HH:MM" strings, so convert on load/save.
const minutesToTime = (mins) => {
  if (mins === null || mins === undefined || mins === '') return '';
  const m = Number(mins);
  const h = Math.floor(m / 60);
  const min = m % 60;
  return `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
};
const timeToMinutes = (time) => {
  if (!time) return null;
  const [h, m] = time.split(':').map(Number);
  return h * 60 + m;
};

const defaultWorkingHours = () =>
  DAYS.map(d => ({ day: d.value, open_time: 480, close_time: 1320, is_off: false }));

const Setting = () => {
  const currency = process.env.REACT_APP_CURRENCY;
  const [isLoader, setIsLoader] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  const [showMapKey, setShowMapKey] = useState(false);
  const [formData, setFormData] = useState({
    ios_version: '',
    android_version: '',
    payment_mode: '',
    service_charges: '',
    free_cancellation_hours: '',
    cancellation_charge_percentage: '',
    late_cancellation_block_hours: '',
    cancellation_policy_text: '',
    map_key: '',
    admin_emails: '',
    membership_approval_window_minutes: '',
    late_arrival_threshold_mins: '',
    no_show_threshold_mins: '',
    booking_buffer_minutes: '',
    tabby_enabled: true,
    tamara_enabled: true,
  });
  const [workingHours, setWorkingHours] = useState(defaultWorkingHours());
  const [iceboxBreak, setIceboxBreak] = useState({ start_time: '', end_time: '' });
  const [venueLocation, setVenueLocation] = useState({ text: '', lat: null, lng: null });

  useEffect(() => {
    getSingleData();
  }, []);

  const getSingleData = async () => {
    setIsLoader(true);
    try {
      const response = await ApiService.request({ method: 'GET', url: 'admin/getSettings' });
      const data = response.data;
      if (data.status && data.data) {
        const s = data.data;
        setFormData(prev => ({
          ...prev,
          ios_version: s.ios_version ?? '',
          android_version: s.android_version ?? '',
          payment_mode: s.payment_mode ?? '',
          service_charges: s.service_charges ?? '',
          free_cancellation_hours: s.free_cancellation_hours ?? '',
          cancellation_charge_percentage: s.cancellation_charge_percentage ?? '',
          late_cancellation_block_hours: s.late_cancellation_block_hours ?? '',
          cancellation_policy_text: s.cancellation_policy_text ?? '',
          map_key: s.map_key ?? '',
          admin_emails: s.admin_emails ?? '',
          membership_approval_window_minutes: s.membership_approval_window_minutes ?? '',
          late_arrival_threshold_mins: s.late_arrival_threshold_mins ?? '',
          no_show_threshold_mins: s.no_show_threshold_mins ?? '',
          booking_buffer_minutes: s.booking_buffer_minutes ?? '',
          tabby_enabled: s.tabby_enabled ?? true,
          tamara_enabled: s.tamara_enabled ?? true,
        }));

        // Always render all seven days, even if the stored array is partial.
        const stored = s.working_hours || [];
        setWorkingHours(
          DAYS.map(d => {
            const row = stored.find(w => w.day === d.value);
            return row
              ? { day: d.value, open_time: row.open_time ?? 480, close_time: row.close_time ?? 1320, is_off: !!row.is_off }
              : { day: d.value, open_time: 480, close_time: 1320, is_off: false };
          })
        );

        setIceboxBreak({
          start_time: s.icebox_break_time?.start_time ?? '',
          end_time: s.icebox_break_time?.end_time ?? '',
        });

        setVenueLocation({
          text: s.venue_location?.address ?? '',
          lat: s.venue_location?.lat ?? null,
          lng: s.venue_location?.lng ?? null,
        });

        // The map picker reads its API key from localStorage (also populated by
        // the admin Header on an interval) — refresh it here too so a freshly
        // saved key is usable on this page without waiting on that poll.
        if (s.map_key) localStorage.setItem('map_key', s.map_key);
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoader(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const adminEmailList = formData.admin_emails
      .split(',')
      .map(email => email.trim())
      .filter(Boolean);
    const invalidEmail = adminEmailList.find(email => !isValidEmail(email));
    if (invalidEmail) {
      toast.error(`Invalid admin email: ${invalidEmail}`);
      return;
    }

    setIsLoader(true);
    try {
      const payload = {
        ...formData,
        admin_emails: adminEmailList.join(', '),
        working_hours: workingHours.map(w => ({
          day: Number(w.day),
          open_time: Number(w.open_time),
          close_time: Number(w.close_time),
          is_off: !!w.is_off,
        })),
        icebox_break_time: iceboxBreak,
        venue_location: {
          address: venueLocation.text || null,
          lat: venueLocation.lat,
          lng: venueLocation.lng,
        },
      };

      const response = await ApiService.request({ method: 'POST', url: 'admin/updateSettings', data: payload });
      const data = response.data;
      if (data.status) toast.success(data.message);
      else toast.error(data.message);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoader(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleToggle = (e) => {
    const { name, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: checked }));
  };

  const handleHourChange = (day, field, value) => {
    setWorkingHours(prev =>
      prev.map(w => (w.day === day ? { ...w, [field]: field === 'is_off' ? value : value } : w))
    );
  };

  return (
    <div className="d-flex flex-column flex-column-fluid">
      <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
        <div id="kt_app_toolbar_container" className="app-container container-xxl d-flex flex-stack">
          <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
            <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">Settings</h1>
          </div>
        </div>
      </div>

      <div id="kt_app_content" className="app-content flex-column-fluid">
        <div id="kt_app_content_container" className="app-container container-xxl">
          <div className="card">
            <div className="card-header border-0 pt-6">
              <ul className="nav nav-tabs nav-line-tabs nav-line-tabs-2x border-0 fs-6 fw-semibold">
                {TABS.map(tab => (
                  <li className="nav-item" key={tab.key}>
                    <button
                      type="button"
                      className={`nav-link ${activeTab === tab.key ? 'active' : ''}`}
                      onClick={() => setActiveTab(tab.key)}
                    >
                      <i className={`bi ${tab.icon} me-2`}></i>{tab.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            <div className="card-body py-6">
              <form onSubmit={handleSubmit}>

                {activeTab === 'general' && (
                  <div className="row">
                    <div className="col-md-6 mb-6">
                      <label className="form-label">iOS Version</label>
                      <input type="text" name="ios_version" className="form-control form-control-solid"
                        value={formData.ios_version} onChange={handleChange} placeholder="1.0.0" />
                    </div>
                    <div className="col-md-6 mb-6">
                      <label className="form-label">Android Version</label>
                      <input type="text" name="android_version" className="form-control form-control-solid"
                        value={formData.android_version} onChange={handleChange} placeholder="1.0.0" />
                    </div>
                    <div className="col-md-6 mb-6">
                      <label className="form-label">Service Charges {currency ? `(${currency})` : ''}</label>
                      <input type="number" min="0" step="0.01" name="service_charges" className="form-control form-control-solid"
                        value={formData.service_charges} onChange={handleChange} placeholder="0" />
                    </div>
                    <div className="col-md-6 mb-6">
                      <label className="form-label">Membership Approval Window (minutes)</label>
                      <input type="number" min="1" step="1" name="membership_approval_window_minutes" className="form-control form-control-solid"
                        value={formData.membership_approval_window_minutes} onChange={handleChange} placeholder="5" />
                    </div>
                    <div className="col-md-6 mb-6">
                      <label className="form-label">Late Arrival Timing (minutes)</label>
                      <input type="number" min="1" name="late_arrival_threshold_mins" className="form-control form-control-solid"
                        value={formData.late_arrival_threshold_mins} onChange={handleChange} placeholder="15" />
                    </div>
                    <div className="col-md-6 mb-6">
                      <label className="form-label">No Show Timing (minutes)</label>
                      <input type="number" min="1" name="no_show_threshold_mins" className="form-control form-control-solid"
                        value={formData.no_show_threshold_mins} onChange={handleChange} placeholder="25" />
                    </div>
                    <div className="col-md-6 mb-6">
                      <label className="form-label">Booking Buffer Time (minutes)</label>
                      <input type="number" min="0" step="1" name="booking_buffer_minutes" className="form-control form-control-solid"
                        value={formData.booking_buffer_minutes} onChange={handleChange} placeholder="5" />
                      <div className="form-text">Reserved for room turnover — shown as time deducted from the session duration on checkout and booking details.</div>
                    </div>
                  </div>
                )}

                {activeTab === 'payments' && (
                  <div className="row">
                    <div className="col-md-12 mb-6">
                      <label className="form-label">Payment Mode</label>
                      <select name="payment_mode" className="form-select form-control-solid"
                        value={formData.payment_mode} onChange={handleChange}>
                        <option value="">Select mode</option>
                        <option value="test">Test</option>
                        <option value="live">Live</option>
                      </select>
                      <div className="form-text">Switches Stripe, Tabby and Tamara between their test and live keys.</div>
                    </div>
                    {[
                      { name: 'tabby_enabled', label: 'Tabby', hint: 'Offer Tabby (pay in installments) on app checkout and membership plans.' },
                      { name: 'tamara_enabled', label: 'Tamara', hint: 'Offer Tamara (pay in installments) on app checkout and membership plans.' },
                    ].map(opt => (
                      <div className="col-md-6 mb-6" key={opt.name}>
                        <div className="d-flex align-items-center justify-content-between border border-dashed rounded p-5">
                          <div className="me-5">
                            <div className="fw-bold fs-6">{opt.label}</div>
                            <div className="form-text mt-1">{opt.hint}</div>
                          </div>
                          <div className="form-check form-switch form-check-custom form-check-solid">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              name={opt.name}
                              checked={!!formData[opt.name]}
                              onChange={handleToggle}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                    <div className="col-12 mb-6">
                      <div className="form-text">Card payments (Stripe) are always available. Changes apply the next time the app opens checkout.</div>
                    </div>
                  </div>
                )}

                {activeTab === 'operating' && (
                  <>
                    <div className="table-responsive mb-8">
                      <table className="table align-middle fs-6 gy-4">
                        <thead>
                          <tr className="text-start text-gray-400 fw-bold fs-7 text-uppercase gs-0">
                            <th>Day</th>
                            <th>Opens</th>
                            <th>Closes</th>
                            <th>Closed</th>
                          </tr>
                        </thead>
                        <tbody className="fw-semibold text-gray-600">
                          {workingHours.map((w) => (
                            <tr key={w.day}>
                              <td className="text-dark fw-bold">{DAYS.find(d => d.value === w.day)?.label}</td>
                              <td style={{ maxWidth: '160px' }}>
                                <input
                                  type="time"
                                  className="form-control form-control-solid"
                                  value={minutesToTime(w.open_time)}
                                  disabled={w.is_off}
                                  onChange={(e) => handleHourChange(w.day, 'open_time', timeToMinutes(e.target.value))}
                                />
                              </td>
                              <td style={{ maxWidth: '160px' }}>
                                <input
                                  type="time"
                                  className="form-control form-control-solid"
                                  value={minutesToTime(w.close_time)}
                                  disabled={w.is_off}
                                  onChange={(e) => handleHourChange(w.day, 'close_time', timeToMinutes(e.target.value))}
                                />
                              </td>
                              <td>
                                <div className="form-check form-switch form-check-custom form-check-solid">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    checked={w.is_off}
                                    onChange={(e) => handleHourChange(w.day, 'is_off', e.target.checked)}
                                  />
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    <h4 className="fw-bold mb-4">Icebox Break Time</h4>
                    <div className="form-text mb-4">Icebox rooms are unbookable during this window.</div>
                    <div className="row">
                      <div className="col-md-4 mb-6">
                        <label className="form-label">Start Time</label>
                        <input
                          type="time"
                          className="form-control form-control-solid"
                          value={iceboxBreak.start_time}
                          onChange={(e) => setIceboxBreak(prev => ({ ...prev, start_time: e.target.value }))}
                        />
                      </div>
                      <div className="col-md-4 mb-6">
                        <label className="form-label">End Time</label>
                        <input
                          type="time"
                          className="form-control form-control-solid"
                          value={iceboxBreak.end_time}
                          onChange={(e) => setIceboxBreak(prev => ({ ...prev, end_time: e.target.value }))}
                        />
                      </div>
                    </div>
                  </>
                )}

                {activeTab === 'map' && (
                  <div className="row">
                    <div className="col-md-8 mb-6">
                      <label className="form-label">Google Map Key</label>
                      <div className="input-group">
                        <input
                          type={showMapKey ? 'text' : 'password'}
                          name="map_key"
                          className="form-control form-control-solid"
                          value={formData.map_key}
                          onChange={handleChange}
                          placeholder="Google Maps API key"
                        />
                        <button type="button" className="btn btn-light" onClick={() => setShowMapKey(v => !v)}>
                          <i className={`bi ${showMapKey ? 'bi-eye-slash' : 'bi-eye'}`}></i>
                        </button>
                      </div>
                   
                    </div>

                    <div className="col-md-10 mb-6">
                      {/* <label className="form-label">Venue Location</label> */}
                      <GoogleMapSelector
                        locationNew={venueLocation}
                        onLocationChanged={(loc) => setVenueLocation({ text: loc.text, lat: loc.lat, lng: loc.lng })}
                      />
                    </div>
                  </div>
                )}

                {activeTab === 'notifications' && (
                  <div className="row">
                    <div className="col-md-8 mb-6">
                      <label className="form-label">Admin Notification Emails</label>
                      <textarea
                        name="admin_emails"
                        rows="2"
                        className="form-control form-control-solid"
                        value={formData.admin_emails}
                        onChange={handleChange}
                        placeholder="admin1@example.com, admin2@example.com"
                      />
                      <div className="form-text">
                        Comma-separated email addresses. These receive an email for every admin
                        notification (new registration, new booking, booking cancellation,
                        subscription purchase, and pending subscription cancellation). The first
                        address is used as the recipient and the rest are CC'd.
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'cancellation' && (
                  <div className="row">
                    <div className="col-md-6 mb-6">
                      <label className="form-label">Free Cancellation Window (hours)</label>
                      <input type="number" min="0" name="free_cancellation_hours" className="form-control form-control-solid"
                        value={formData.free_cancellation_hours} onChange={handleChange} placeholder="0" />
                      <div className="form-text">Cancel within this window and no charge applies.</div>
                    </div>
                    <div className="col-md-6 mb-6">
                      <label className="form-label">Cancellation Charge (%)</label>
                      <input type="number" min="0" max="100" name="cancellation_charge_percentage" className="form-control form-control-solid"
                        value={formData.cancellation_charge_percentage} onChange={handleChange} placeholder="0" />
                      <div className="form-text">Applied to per-session bookings cancelled inside the window.</div>
                    </div>
                    <div className="col-md-6 mb-6">
                      <label className="form-label">Late Cancellation Re-booking Block (hours)</label>
                      <input type="number" min="0" name="late_cancellation_block_hours" className="form-control form-control-solid"
                        value={formData.late_cancellation_block_hours} onChange={handleChange} placeholder="0" />
                      <div className="form-text">Subscription bookings cancelled inside the window block the user from booking again for this many hours. Defaults to the free window when 0.</div>
                    </div>
                    <div className="col-md-12 mb-6">
                      <label className="form-label">Late Cancellation &amp; No-Show Policy Text</label>
                      <textarea
                        name="cancellation_policy_text"
                        rows="4"
                        className="form-control form-control-solid"
                        value={formData.cancellation_policy_text}
                        onChange={handleChange}
                        placeholder="Per-session bookings are fully refunded when cancelled before the cancellation window..."
                      />
                      <div className="form-text">Shown on the mobile app's Booking Detail screen, above the Cancel Booking button.</div>
                    </div>
                  </div>
                )}

                <div className="d-flex justify-content-end">
                  <button type="submit" className="btn btn-base" disabled={isLoader}>
                    {isLoader ? <Loader text="Saving" size="20" /> : 'Save Settings'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Setting;
